import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  Stack,
  StackDivider,
  Button,
  VStack,
  HStack,
  Flex,
  Badge,
  Icon,
  useToast,
  useColorModeValue,
  Tooltip,
  Divider,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { FaSpotify, FaApple, FaMusic, FaCheckCircle, FaSignOutAlt, FaUserCircle, FaInfo } from 'react-icons/fa';
import AppHeader from '../components/AppHeader';
import { useAuth } from '../contexts/AuthContext';
import { useSpotify } from '../context/SpotifyContext';
import { useAppleMusic } from '../context/AppleMusicContext';
import { useMusicService } from '../context/MusicServiceContext';
import { MusicServiceType } from '../context/MusicServiceContext';
import { deleteAuthToken } from '../lib/tokenStorage';
import MusicServiceToggle from '../components/MusicServiceToggle';

// Spotify service icon component
const SpotifyIcon = () => (
  <Icon as={FaSpotify} color="#1DB954" boxSize={6} />
);

// Apple Music icon component
const AppleMusicIcon = () => (
  <Icon as={FaApple} color="#FA2D48" boxSize={6} />
);

const Settings = () => {
  const { user, userProfile } = useAuth();
  const { 
    isAuthenticated: isSpotifyAuthenticated, 
    login: spotifyLogin, 
    logout: spotifyLogout 
  } = useSpotify();
  const { 
    isAuthenticated: isAppleAuthenticated, 
    login: appleLogin, 
    logout: appleLogout 
  } = useAppleMusic();
  const { currentService, setCurrentService } = useMusicService();
  
  const toast = useToast();
  const bgCard = useColorModeValue('white', 'gray.800');
  const bgSection = useColorModeValue('gray.50', 'gray.900');
  
  // For confirmation dialog
  const { isOpen: isSpotifyDialogOpen, onOpen: onSpotifyDialogOpen, onClose: onSpotifyDialogClose } = useDisclosure();
  const { isOpen: isAppleDialogOpen, onOpen: onAppleDialogOpen, onClose: onAppleDialogClose } = useDisclosure();
  const cancelRef = React.useRef(null);

  // Handle Spotify logout with confirmation
  const handleSpotifyLogout = async () => {
    try {
      await spotifyLogout();
      onSpotifyDialogClose();
      toast({
        title: "Spotify disconnected",
        description: "Your Spotify account has been disconnected. The connection has also been removed from our database.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error disconnecting Spotify:", error);
      toast({
        title: "Error disconnecting",
        description: "There was an error disconnecting your Spotify account. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle Apple Music logout with confirmation
  const handleAppleMusicLogout = async () => {
    try {
      await appleLogout();
      onAppleDialogClose();
      toast({
        title: "Apple Music disconnected",
        description: "Your Apple Music account has been disconnected. The connection has also been removed from our database.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error disconnecting Apple Music:", error);
      toast({
        title: "Error disconnecting",
        description: "There was an error disconnecting your Apple Music account. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle service change for the MusicServiceToggle component
  const handleServiceChange = (service: MusicServiceType) => {
    setCurrentService(service);
    toast({
      title: `${service === 'spotify' ? 'Spotify' : 'Apple Music'} selected`,
      description: `${service === 'spotify' ? 'Spotify' : 'Apple Music'} is now your default music service.`,
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box minH="100vh" bg={bgSection}>
      <AppHeader pageTitle="Settings" />
      
      <Container maxW="container.lg" pt="100px" pb="50px">
        <Heading size="xl" mb={8}>Settings</Heading>
        
        {/* User Section */}
        <Card bg={bgCard} shadow="md" mb={6}>
          <CardHeader>
            <Heading size="md">
              <HStack>
                <Icon as={FaUserCircle} />
                <Text>User Account</Text>
              </HStack>
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack align="start" spacing={4}>
              <Box>
                <Text fontWeight="bold">Email</Text>
                <Text>{user?.email}</Text>
              </Box>
            </VStack>
          </CardBody>
        </Card>
        
        {/* Music Services Section */}
        <Card bg={bgCard} shadow="md" mb={6}>
          <CardHeader>
            <Heading size="md">
              <HStack>
                <Icon as={FaMusic} />
                <Text>Music Services</Text>
              </HStack>
            </Heading>
            <Text fontSize="sm" mt={2} mb={4} color="gray.500">
              Connect your music streaming accounts to enable synchronized lyrics. 
              When you disconnect a service, the connection will also be removed from our database.
            </Text>
            
            {/* Quick Service Switcher */}
            <Box mt={4} mb={2}>
              <Text fontSize="sm" fontWeight="medium" mb={2}>Quick Service Switch:</Text>
              <MusicServiceToggle
                isSpotifySelected={currentService === 'spotify'}
                isAppleSelected={currentService === 'apple'}
                isSpotifyAuthenticated={isSpotifyAuthenticated}
                isAppleAuthenticated={isAppleAuthenticated}
                handleServiceChange={handleServiceChange}
              />
            </Box>
          </CardHeader>
          
          <CardBody>
            <Stack divider={<StackDivider />} spacing={4}>
              {/* Spotify Section */}
              <Box py={2}>
                <Flex justify="space-between" align="center">
                  <HStack>
                    <SpotifyIcon />
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold">Spotify</Text>
                      {isSpotifyAuthenticated && (
                        <Badge colorScheme="green">Connected</Badge>
                      )}
                    </VStack>
                  </HStack>
                  
                  <HStack>
                    {isSpotifyAuthenticated ? (
                      <>
                        <Button
                          leftIcon={<Icon as={FaCheckCircle} />}
                          colorScheme="green"
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentService('spotify')}
                          isDisabled={currentService === 'spotify'}
                        >
                          {currentService === 'spotify' ? 'Current Default' : 'Set as Default'}
                        </Button>
                        <Button
                          leftIcon={<Icon as={FaSignOutAlt} />}
                          colorScheme="red"
                          variant="outline"
                          size="sm"
                          onClick={onSpotifyDialogOpen}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        colorScheme="green"
                        onClick={spotifyLogin}
                      >
                        Connect
                      </Button>
                    )}
                  </HStack>
                </Flex>
                
                {isSpotifyAuthenticated && (
                  <Text fontSize="sm" mt={3} color="gray.500">
                    <Icon as={FaInfo} mr={1} />
                    Your Spotify connection allows SyncdIt to access your currently playing track and control playback.
                  </Text>
                )}
              </Box>
              
              {/* Apple Music Section */}
              <Box py={2}>
                <Flex justify="space-between" align="center">
                  <HStack>
                    <AppleMusicIcon />
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold">Apple Music</Text>
                      {isAppleAuthenticated && (
                        <Badge colorScheme="red">Connected</Badge>
                      )}
                    </VStack>
                  </HStack>
                  
                  <HStack>
                    {isAppleAuthenticated ? (
                      <>
                        <Button
                          leftIcon={<Icon as={FaCheckCircle} />}
                          colorScheme="red"
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentService('apple')}
                          isDisabled={currentService === 'apple'}
                        >
                          {currentService === 'apple' ? 'Current Default' : 'Set as Default'}
                        </Button>
                        <Button
                          leftIcon={<Icon as={FaSignOutAlt} />}
                          colorScheme="red"
                          variant="outline"
                          size="sm"
                          onClick={onAppleDialogOpen}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        colorScheme="red"
                        onClick={appleLogin}
                      >
                        Connect
                      </Button>
                    )}
                  </HStack>
                </Flex>
                
                {isAppleAuthenticated && (
                  <Text fontSize="sm" mt={3} color="gray.500">
                    <Icon as={FaInfo} mr={1} />
                    Your Apple Music connection allows SyncdIt to access your currently playing track and control playback.
                  </Text>
                )}
              </Box>
            </Stack>
          </CardBody>
        </Card>
        
        {/* Spotify Disconnect Confirmation Dialog */}
        <AlertDialog
          isOpen={isSpotifyDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={onSpotifyDialogClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Disconnect Spotify
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to disconnect your Spotify account? This will remove the connection from our database and you'll need to reconnect to use Spotify with SyncdIt.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onSpotifyDialogClose}>
                  Cancel
                </Button>
                <Button colorScheme="red" onClick={handleSpotifyLogout} ml={3}>
                  Disconnect
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
        
        {/* Apple Music Disconnect Confirmation Dialog */}
        <AlertDialog
          isOpen={isAppleDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={onAppleDialogClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Disconnect Apple Music
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to disconnect your Apple Music account? This will remove the connection from our database and you'll need to reconnect to use Apple Music with SyncdIt.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onAppleDialogClose}>
                  Cancel
                </Button>
                <Button colorScheme="red" onClick={handleAppleMusicLogout} ml={3}>
                  Disconnect
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Container>
    </Box>
  );
};

export default Settings; 